"use client";

import { useEffect, useState } from "react";
import { railSections } from "@/config/navigation";
import { cn } from "@/lib/utils";

export function SectionRail() {
  const [activeId, setActiveId] = useState<string>(railSections[0]?.id ?? "hero");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const elements = railSections
      .map((section) => document.getElementById(section.id))
      .filter((element): element is HTMLElement => element !== null);

    if (elements.length === 0) return;

    if (typeof IntersectionObserver === "undefined") {
      setActiveId(railSections[0].id);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const activeTheme = railSections.find((section) => section.id === activeId)?.theme ?? "ink";

  return (
    <nav
      aria-label="Rejstřík sekcí"
      className="section-rail fixed left-0 top-0 z-[90] h-screen w-[72px] flex-col items-center justify-center gap-1"
      data-theme={activeTheme}
    >
      <span
        aria-hidden="true"
        className="absolute right-0 top-0 h-full w-px bg-[var(--color-hairline)]"
      >
        <span
          className="block h-full w-full origin-top bg-[var(--color-blue)] transition-transform duration-150 ease-linear"
          style={{ transform: `scaleY(${progress})` }}
        />
      </span>

      {railSections.map((section) => {
        const isActive = section.id === activeId;
        return (
          <a
            key={section.id}
            href={`#${section.id}`}
            aria-current={isActive ? "true" : undefined}
            className={cn(
              "font-mono text-[12px] leading-none tracking-[0.04em] transition-colors duration-[var(--duration-fast)]",
              "px-2 py-1.5",
              activeTheme === "paper"
                ? isActive
                  ? "text-blue"
                  : "text-paper-muted/70 hover:text-paper-text"
                : isActive
                  ? "text-ice"
                  : "text-text-faint hover:text-text",
            )}
          >
            §{section.index}
          </a>
        );
      })}
    </nav>
  );
}
